import type { AboutOrderSopSection } from "../types";

const BEFORE_YOU_START_SECTION: AboutOrderSopSection = {
  id: "before-you-start",
  titleKey: "about-order-sop-before-start-title",
  titleFallback: "Before you start",
  steps: [
    {
      bodyKey: "about-order-sop-before-start-1",
      bodyFallback:
        "Sign in, then open Orders and choose Sales. Your account needs access to create and manage orders through to completion."
    },
    {
      bodyKey: "about-order-sop-before-start-2",
      bodyFallback:
        "Have a customer ready. Company admins also need to know which store the sale is for. The products you want to sell must already be in stock. You can add them to the order by picking from a list or by scanning the QR sticker printed when stock was received."
    },
    {
      bodyKey: "about-order-sop-before-start-3",
      bodyFallback:
        "Each order moves through a few simple stages, from a working draft to a finished sale."
    }
  ]
};

const ORDER_STATUSES_SECTION: AboutOrderSopSection = {
  id: "order-statuses",
  titleKey: "about-order-sop-order-statuses-title",
  titleFallback: "What each order status means",
  introKey: "about-order-sop-order-statuses-intro",
  introFallback:
    "Every order shows one of seven statuses. Here is what each label means.",
  steps: [
    {
      bodyKey: "about-order-sop-order-statuses-1",
      bodyFallback:
        "Draft: You just started the order. Add products, change the customer or store, or scan items. When you are ready, tap Confirm Order. To throw it away, tap Cancel Order."
    },
    {
      bodyKey: "about-order-sop-order-statuses-2",
      bodyFallback:
        "Offer suggested: Same as Draft, but used when you shared a price quote with the customer. You can still add items, make changes, confirm, or cancel."
    },
    {
      bodyKey: "about-order-sop-order-statuses-3",
      bodyFallback:
        "Offer accepted: The customer agreed to buy. You can now create an invoice. You may still cancel if you need to."
    },
    {
      bodyKey: "about-order-sop-order-statuses-4",
      bodyFallback:
        "Pending payment: The invoice was sent. You are waiting for money from the customer. Use Record Payment when they pay."
    },
    {
      bodyKey: "about-order-sop-order-statuses-5",
      bodyFallback:
        "Paid: The customer has paid in full. You will see links to view the invoice and each receipt. You can now finish the sale with Complete Order."
    },
    {
      bodyKey: "about-order-sop-order-statuses-6",
      bodyFallback:
        "Completed: The sale is done. Stock has been updated and the order can no longer be changed."
    },
    {
      bodyKey: "about-order-sop-order-statuses-7",
      bodyFallback:
        "Cancelled: The order was cancelled and items put back into stock. Old drafts that are left open too long may be cancelled automatically."
    }
  ],
  supplementalNotes: [
    {
      bodyKey: "about-order-sop-order-statuses-note-path",
      bodyFallback:
        "Most sales follow this path: Draft, add items, Offer accepted, Pending payment, Paid, then Completed."
    },
    {
      bodyKey: "about-order-sop-order-statuses-note-invoice",
      bodyFallback:
        "Invoice payment status is separate from the order status. On the invoice you will see Unpaid, Partially paid, or Paid. That tracks how much money was received. While the order shows Pending payment, the invoice may say Unpaid or Partially paid until the full amount is in. Only then does the order change to Paid."
    }
  ]
};

const OPEN_SALES_ORDERS_SECTION: AboutOrderSopSection = {
  id: "open-sales-orders",
  titleKey: "about-order-sop-open-sales-orders-title",
  titleFallback: "Open Sales Orders",
  steps: [
    {
      bodyKey: "about-order-sop-open-sales-orders-1",
      bodyFallback:
        "In the menu on the left, click Orders. You will see a list of sales with the order number, total, status, and date.",
      imageId: "sales-orders"
    },
    {
      bodyKey: "about-order-sop-open-sales-orders-2",
      bodyFallback:
        "Use search to find an order by number, customer, or status. When you are ready to ring up a new sale, click the purple New Order button."
    }
  ]
};

const CREATE_ORDER_SECTION: AboutOrderSopSection = {
  id: "create-order",
  titleKey: "about-order-sop-create-order-title",
  titleFallback: "Start the sale",
  steps: [
    {
      bodyKey: "about-order-sop-create-order-1",
      bodyFallback:
        "Click New Order. A window opens to start the sale. Fields with * must be filled in."
    },
    {
      bodyKey: "about-order-sop-create-order-2",
      bodyFallback:
        "Company admins choose the Store where the sale happens. Store staff are already linked to their store, so they will not see a store field and only need to pick the customer.",
      imageId: "create-order-store"
    },
    {
      bodyKey: "about-order-sop-create-order-3",
      bodyFallback:
        "Everyone selects the customer. Search by name, phone, or email and pick them from the list.",
      imageId: "create-order-customer"
    },
    {
      bodyKey: "about-order-sop-create-order-4",
      bodyFallback:
        "Click Create Order. You land on the new order page. It will show Draft at the top."
    }
  ]
};

const NEW_CUSTOMER_SECTION: AboutOrderSopSection = {
  id: "new-customer",
  titleKey: "about-order-sop-new-customer-title",
  titleFallback: "Add a new customer (if needed)",
  steps: [
    {
      bodyKey: "about-order-sop-new-customer-1",
      bodyFallback:
        "Customer not on the list? Click the + button next to Customer to add someone new.",
      imageId: "new-customer"
    },
    {
      bodyKey: "about-order-sop-new-customer-2",
      bodyFallback:
        "Fill in their name, choose Individual or Business, and add a phone number or email. Save, then pick them in the Create Sales Order window."
    }
  ]
};

const DRAFT_ORDER_SECTION: AboutOrderSopSection = {
  id: "draft-order",
  titleKey: "about-order-sop-draft-order-title",
  titleFallback: "Review the draft order",
  steps: [
    {
      bodyKey: "about-order-sop-draft-order-1",
      bodyFallback:
        "You will see the order number, customer, store, and a Draft label. No products are on the order yet.",
      imageId: "draft-order"
    },
    {
      bodyKey: "about-order-sop-draft-order-2",
      bodyFallback:
        "Use Edit Order to fix the customer. Company admins can also change the store. Use Delete if you opened this order by mistake."
    }
  ]
};

const ADD_ITEMS_SECTION: AboutOrderSopSection = {
  id: "add-items",
  titleKey: "about-order-sop-add-items-title",
  titleFallback: "Add items to the order",
  steps: [
    {
      bodyKey: "about-order-sop-add-items-1",
      bodyFallback:
        "There are two ways to add products: pick them from a list, or scan the QR sticker on each item (the same stickers you printed when stock was added to the shop)."
    },
    {
      bodyKey: "about-order-sop-add-items-2",
      bodyFallback:
        "To pick manually: click Add items, choose the product, then choose the exact item from stock.",
      imageId: "add-items-modal"
    },
    {
      bodyKey: "about-order-sop-add-items-3",
      bodyFallback:
        "To scan: you will need a handheld barcode scanner (sometimes called a barcode gun). Kivunova FMS works with common scanners. Plug it into your computer, click the scan box on the order page, and scan the QR sticker. Each beep adds one item.",
      imageId: "barcode-scanner"
    },
    {
      bodyKey: "about-order-sop-add-items-4",
      bodyFallback:
        "No scanner yet? You can type the code from the sticker into the scan box, press Enter, then click Add Item.",
      imageId: "barcode-input"
    }
  ]
};

const REVIEW_CONFIRM_SECTION: AboutOrderSopSection = {
  id: "review-confirm",
  titleKey: "about-order-sop-review-confirm-title",
  titleFallback: "Review totals and confirm",
  steps: [
    {
      bodyKey: "about-order-sop-review-confirm-1",
      bodyFallback:
        "Check each line for quantity, price, discounts, and line total. Tap a row to change a discount or remove an item.",
      imageId: "review-confirm"
    },
    {
      bodyKey: "about-order-sop-review-confirm-2",
      bodyFallback:
        "Look at the subtotal, tax, and total at the bottom. Happy with everything? Click Confirm Order. Changed your mind? Click Cancel Order."
    }
  ]
};

const ISSUE_INVOICE_SECTION: AboutOrderSopSection = {
  id: "issue-invoice",
  titleKey: "about-order-sop-issue-invoice-title",
  titleFallback: "Create an invoice",
  steps: [
    {
      bodyKey: "about-order-sop-issue-invoice-1",
      bodyFallback:
        "Once the order shows Offer accepted, click Issue Invoice at the top. This is available when the order has at least one product with a price and no invoice exists yet.",
      imageId: "offer-accepted"
    },
    {
      bodyKey: "about-order-sop-issue-invoice-2",
      bodyFallback:
        "Confirm when asked. An invoice is created for the customer with the same totals as the order.",
      imageId: "issue-invoice"
    },
    {
      bodyKey: "about-order-sop-issue-invoice-3",
      bodyFallback:
        "The order now shows Pending payment. Click View Invoice to open the bill. It starts as Unpaid until the customer pays.",
      imageId: "invoice-unpaid"
    }
  ]
};

const RECORD_PAYMENT_SECTION: AboutOrderSopSection = {
  id: "record-payment",
  titleKey: "about-order-sop-record-payment-title",
  titleFallback: "Record payment (full or partial)",
  steps: [
    {
      bodyKey: "about-order-sop-record-payment-1",
      bodyFallback:
        "When the customer pays, click Record Payment on the order or on the invoice. A form opens showing how much is still owed.",
      imageId: "create-payment"
    },
    {
      bodyKey: "about-order-sop-record-payment-2",
      bodyFallback:
        "Enter how much they paid, how they paid (cash, mobile money, or bank deposit), and the date. Reference number and notes are optional. Click Create Payment to save."
    },
    {
      bodyKey: "about-order-sop-record-payment-3",
      bodyFallback:
        "A receipt is saved for each payment. You can enter the full amount at once or a smaller amount if they pay in instalments."
    },
    {
      bodyKey: "about-order-sop-record-payment-4",
      bodyFallback:
        "If they pay less than the full total, the invoice shows Partially paid and lists what has been received so far. The order stays at Pending payment until the rest is paid.",
      imageId: "invoice-partial-paid"
    },
    {
      bodyKey: "about-order-sop-record-payment-5",
      bodyFallback:
        "Keep recording payments until nothing is left to pay. When the balance reaches zero, the invoice shows Paid and the order moves to Paid.",
      imageId: "order-paid"
    },
    {
      bodyKey: "about-order-sop-record-payment-6",
      bodyFallback:
        "On a paid order you can open the invoice or each receipt. The Record Payment button goes away once everything is settled."
    }
  ]
};

const COMPLETE_ORDER_SECTION: AboutOrderSopSection = {
  id: "complete-order",
  titleKey: "about-order-sop-complete-order-title",
  titleFallback: "Finish the sale",
  steps: [
    {
      bodyKey: "about-order-sop-complete-order-1",
      bodyFallback:
        "After the order shows Paid, take one last look at the items and totals.",
      imageId: "complete-order"
    },
    {
      bodyKey: "about-order-sop-complete-order-2",
      bodyFallback:
        "Click Complete Order to close the sale. The status changes to Completed. Print the order or open a receipt if the customer wants a paper copy."
    }
  ]
};

export const ORDER_RUN_BOOK_BEFORE_YOU_START_SECTION = BEFORE_YOU_START_SECTION;

export const ORDER_RUN_BOOK_ORDER_STATUSES_SECTION = ORDER_STATUSES_SECTION;

export const ORDER_RUN_BOOKS_SHARED_SECTIONS: AboutOrderSopSection[] = [
  BEFORE_YOU_START_SECTION,
  ORDER_STATUSES_SECTION
];

export const CREATE_ORDER_SOP_SECTIONS: AboutOrderSopSection[] = [
  OPEN_SALES_ORDERS_SECTION,
  CREATE_ORDER_SECTION,
  NEW_CUSTOMER_SECTION
];

export const MANAGE_ORDER_SOP_SECTIONS: AboutOrderSopSection[] = [
  DRAFT_ORDER_SECTION,
  ADD_ITEMS_SECTION,
  REVIEW_CONFIRM_SECTION
];

export const ISSUE_INVOICE_SOP_SECTIONS: AboutOrderSopSection[] = [
  ISSUE_INVOICE_SECTION
];

export const RECORD_PAYMENT_SOP_SECTIONS: AboutOrderSopSection[] = [
  RECORD_PAYMENT_SECTION,
  COMPLETE_ORDER_SECTION
];
